// Staff editor for a still-draft order's line items: change quantities, override unit prices, remove
// lines, and search-to-add products from the same catalog the order was placed against. Saving pushes
// the changes to the server (which re-prices, syncs the Shopify draft, and rebases the payment schedule).
import { useMemo, useRef, useState } from 'react';
import { searchCatalog } from '../api/catalog.js';
import { updateOrderLines } from '../api/orders.js';
import { fmtMoney, sizeOf, qtyError, capForClient } from '../lib/matrix.js';
import { useAuthStore } from '../stores/authStore.js';

// allowPriceOverride: staff may change a line's unit price; clients/reps editing their pending order may not.
export default function OrderLineEditor({ order, onSaved, onCancel, allowPriceOverride = true }) {
  const currency = order.currency;
  const scope = { store: order.store, companyLocationId: order.client?.shopifyCompanyLocationId };

  // Each row is an editable copy of a line. `catalogPrice`/`rule` are known only for lines added via
  // search (existing lines were catalog-priced at creation; the server re-validates either way).
  const [rows, setRows] = useState(() =>
    (order.lineItems || []).map((l) => ({
      variantId: l.variantId,
      title: l.title,
      sku: l.sku,
      size: sizeOf(l),
      quantity: String(l.quantity ?? ''),
      unitPrice: l.unitPrice != null ? String(l.unitPrice) : '',
      catalogPrice: null,
      rule: null,
    }))
  );
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lineErrors, setLineErrors] = useState([]); // [{ variantId, error }]
  const debounceRef = useRef();

  const onSearch = (v) => {
    setQuery(v);
    clearTimeout(debounceRef.current);
    if (!v.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setSearching(true);
      searchCatalog(scope, { q: v.trim() })
        .then((r) => {
          setResults(r.products || []);
          setOpen(true);
        })
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
  };

  const viewerRole = useAuthStore((st) => st.user?.role);
  // A client editing their pending order is capped at what the warehouse can supply (staff/reps aren't).
  const capped = (v) => (viewerRole === 'CLIENT' ? capForClient(v) : v);

  const addVariant = (product, v) => {
    if (v.locked) return;
    setRows((prev) => {
      if (prev.some((r) => r.variantId === v.id)) return prev; // already on the order
      return [
        ...prev,
        {
          variantId: v.id,
          title: product.title,
          sku: v.sku,
          size: sizeOf(v),
          quantity: String(v.quantityRule?.min || 1),
          unitPrice: v.price != null ? String(v.price) : '',
          catalogPrice: v.price ?? null,
          rule: v.quantityRule || null,
        },
      ];
    });
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const setRow = (i, patch) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const errFor = (variantId) => lineErrors.find((e) => e.variantId === variantId)?.error || null;

  const totals = useMemo(() => {
    let units = 0;
    let subtotal = 0;
    for (const r of rows) {
      const q = Number(r.quantity) || 0;
      const p = Number(r.unitPrice) || 0;
      units += q;
      subtotal += q * p;
    }
    return { units, subtotal: Math.round(subtotal * 100) / 100 };
  }, [rows]);

  const save = async () => {
    setError('');
    setLineErrors([]);
    if (!rows.length) {
      setError('An order needs at least one product. Cancel to leave it unchanged, or add a product.');
      return;
    }
    const lines = rows.map((r) => ({
      variantId: r.variantId,
      quantity: Number(r.quantity),
      unitPrice: !allowPriceOverride || r.unitPrice === '' ? undefined : Number(r.unitPrice),
    }));
    if (lines.some((l) => !Number.isInteger(l.quantity) || l.quantity <= 0)) {
      setError('Every line needs a whole-number quantity of 1 or more.');
      return;
    }
    if (lines.some((l) => l.unitPrice != null && (!Number.isFinite(l.unitPrice) || l.unitPrice < 0))) {
      setError('Prices must be zero or more.');
      return;
    }
    setSaving(true);
    try {
      const updated = await updateOrderLines(order.id, { lines });
      onSaved(updated);
    } catch (e) {
      const err = e.response?.data?.error;
      setError(err?.message || 'Could not save the changes.');
      if (err?.details?.lineErrors) setLineErrors(err.details.lineErrors);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-accent/40 bg-surface shadow-sm">
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <h3 className="text-sm font-semibold text-text-primary">Edit order</h3>
        <span className="text-xs text-text-muted">Changes sync to Shopify on save</span>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-line bg-surface-raised/60 text-left text-xs text-text-muted">
              <th className="px-4 py-2 font-medium">Style</th>
              <th className="px-4 py-2 font-medium">Size</th>
              <th className="px-4 py-2 font-medium">SKU</th>
              <th className="px-3 py-2 text-right font-medium">Qty</th>
              <th className="px-3 py-2 text-right font-medium">Unit price</th>
              <th className="px-4 py-2 text-right font-medium">Line total</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const q = Number(r.quantity) || 0;
              const p = Number(r.unitPrice) || 0;
              const ruleErr = qtyError(q, r.rule);
              const rowErr = errFor(r.variantId);
              return (
                <tr key={r.variantId} className="border-b border-line last:border-0 align-top">
                  <td className="px-4 py-2 text-text-primary">{r.title}</td>
                  <td className="px-4 py-2 text-text-secondary">{r.size}</td>
                  <td className="px-4 py-2 font-mono text-xs text-text-muted">{r.sku}</td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={r.quantity}
                      onChange={(e) => setRow(i, { quantity: e.target.value })}
                      className="w-20 rounded-md border border-line bg-surface px-2 py-1 text-right tabular-nums focus:border-accent focus:outline-none"
                    />
                    {(ruleErr || rowErr) && <div className="mt-0.5 text-[11px] text-error">{rowErr || ruleErr}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {allowPriceOverride ? (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={r.unitPrice}
                        onChange={(e) => setRow(i, { unitPrice: e.target.value })}
                        className="w-24 rounded-md border border-line bg-surface px-2 py-1 text-right tabular-nums focus:border-accent focus:outline-none"
                      />
                    ) : (
                      <span className="tabular-nums text-text-secondary">{fmtMoney(p, currency)}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-medium tabular-nums">{fmtMoney(Math.round(q * p * 100) / 100, currency)}</td>
                  <td className="px-2 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      title="Remove line"
                      className="rounded-md px-2 py-1 text-text-muted hover:bg-error/10 hover:text-error"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-sm text-text-muted">
                  No products. Search below to add one.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="bg-surface-raised/40 text-sm font-semibold">
              <td className="px-4 py-2" colSpan={3}>
                Subtotal (goods)
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{totals.units}</td>
              <td />
              <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(totals.subtotal, currency)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Search-to-add */}
      <div className="border-t border-line p-4">
        <label className="mb-1 block text-xs font-medium text-text-muted">Add a product</label>
        <div className="relative">
          <input
            value={query}
            onChange={(e) => onSearch(e.target.value)}
            onFocus={() => results.length && setOpen(true)}
            placeholder="Search style or SKU…"
            className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
          />
          {searching && <span className="absolute right-3 top-2.5 text-xs text-text-muted">…</span>}
          {open && results.length > 0 && (
            <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-line bg-surface shadow-lg">
              {results.map((p) => (
                <div key={p.id || p.title} className="border-b border-line last:border-0">
                  <div className="px-3 py-1.5 text-xs font-medium text-text-secondary">{p.title}</div>
                  <div className="flex flex-wrap gap-1 px-3 pb-2">
                    {p.variants.map(capped).map((v) => {
                      const already = rows.some((r) => r.variantId === v.id);
                      return (
                        <button
                          key={v.id}
                          type="button"
                          disabled={v.locked || already}
                          onClick={() => addVariant(p, v)}
                          title={v.locked ? 'Unavailable' : already ? 'Already on the order' : `Add ${sizeOf(v)}`}
                          className="rounded-md border border-line px-2 py-1 text-xs hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {sizeOf(v)} · {fmtMoney(v.price, currency)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && <div className="mx-4 mb-3 rounded-md border border-error/40 bg-error/10 px-3 py-2 text-sm text-error">{error}</div>}

      <div className="flex items-center justify-end gap-2 border-t border-line px-4 py-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-md border border-line px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-raised disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
